import BigNumber from 'bignumber.js'
import clsx from 'clsx'
import { format } from 'date-fns'
import IconChevronDown from '@/components/icons/IconChevronDown'
import EditInput from '@/components/trade/EditInput'
import fetcher from '@/api/fetcher'
import numberFormat from '@/utils/numberFormat'
import {
  ORDER_SIDE,
  ORDER_TYPE_MAP,
  ORDER_TYPE_TEXT_MAP,
  ORDER_STATUS_TEXT_MAP,
  MARKET_ORDER_TYPE_LIST
} from '@/constants/order'

function formatValue(v) {
  return [null, '', 'NaN'].includes(v) ? '-' : numberFormat(v, 'max8')
}

export default function OrderHistory({
  isAllOrder,
  isOpenOrder,
  isOrderDetailActive,
  editOrderKey,
  history,
  orderDetailList,
  getOrderDetail = () => {},
  toggleOrderDetail = () => {},
  setEditOrderKey = () => {},
  cancelOrder = () => {},
  resetEditOrderKey = () => {}
}) {
  const {
    order_id,
    client_order_id,
    broker_id,
    symbol,
    side,
    type,
    reduce_only,
    status,
    price,
    average_executed_price,
    quantity,
    executed,
    visible,
    total_fee,
    fee_asset,
    updated_time
  } = history

  const [marketType, baseCurrency, quoteCurrency] = symbol.split('_')
  const marketSymbol =
    marketType === 'PERP'
      ? `${baseCurrency}-PERP`
      : `${baseCurrency}/${quoteCurrency}`
  const isFilledOrder = status === 'FILLED'
  const isBuyOrder = side === ORDER_SIDE.buy
  const isHiddenOrder =
    visible !== quantity && !MARKET_ORDER_TYPE_LIST.includes(type)
  const orderPrice = isFilledOrder ? average_executed_price : price
  const orderQuantity = isFilledOrder ? executed : quantity
  const orderValue = BigNumber(orderPrice).times(orderQuantity).toFixed()
  const orderStatus =
    ORDER_STATUS_TEXT_MAP[status] || status.replace('_', ' ').toLowerCase()
  const onEditOrder = (key, value) => {
    const data = {
      order_id,
      symbol,
      side,
      order_type: type,
      order_price: price,
      order_quantity: quantity,
      [key]: value
    }
    if (client_order_id) {
      data.client_order_id = client_order_id
    }
    if (broker_id) {
      data.broker_id = broker_id
    }
    if (isHiddenOrder) {
      data.visible_quantity = visible
    }
    if (reduce_only) {
      data.reduce_only = reduce_only
    }
    return fetcher.editOrder(data)
  }

  const hasDetail = BigNumber(executed).gt(0)
  const dateColumnPadding = isAllOrder ? 8 : isOpenOrder ? 6 : 7
  const OrderDetailRowHeader = orderDetailList ? (
    <tr className="table-list-expand-header">
      <td className="table-list-expand-cell" colSpan="3" />
      <td className="table-list-expand-cell">Filled price</td>
      <td className="table-list-expand-cell">Filled size</td>
      <td className="table-list-expand-cell">Fee</td>
      <td className="table-list-expand-cell" colSpan={dateColumnPadding} />
      <td className="table-list-expand-cell align-right">Date</td>
      <td className="table-list-expand-cell" />
      {isOpenOrder && <td className="table-list-expand-cell" />}
    </tr>
  ) : null
  const OrderDetailRowList = orderDetailList
    ? orderDetailList.map(
        ({
          id,
          executed_price,
          executed_quantity,
          executed_timestamp,
          fee,
          fee_asset
        }) => (
          <tr key={id} className="table-list-expand-row">
            <td className="table-list-expand-cell" colSpan="3" />
            <td className="table-list-expand-cell">
              {formatValue(executed_price)}
            </td>
            <td className="table-list-expand-cell">
              {formatValue(executed_quantity)}
            </td>
            <td className="table-list-expand-cell">
              {fee} <span className="fee-asset-label">{fee_asset}</span>
            </td>
            <td
              className="table-list-expand-cell"
              colSpan={dateColumnPadding}
            />
            <td className="table-list-expand-cell align-right">
              {format(new Date(executed_timestamp), 'yyyy-MM-dd HH:mm:ss')}
            </td>
            <td className="table-list-expand-cell" />
            {isOpenOrder && <td className="table-list-expand-cell" />}
          </tr>
        )
      )
    : []

  return (
    <>
      <tr
        className={clsx('table-list-row', {
          active: isOrderDetailActive
        })}
      >
        <td className="table-list-cell">{marketSymbol}</td>
        <td className="table-list-cell capitalize order-type-column">
          {ORDER_TYPE_TEXT_MAP[type]}
        </td>
        <td
          className={clsx('table-list-cell align-left order-side-column', {
            'color-positive': isBuyOrder,
            'color-negative': !isBuyOrder
          })}
        >
          {side}
        </td>
        <td className="table-list-cell">
          {!isOpenOrder ? (
            formatValue(price)
          ) : (
            <EditInput
              type="order_price"
              symbol={symbol}
              value={price}
              isFocus={editOrderKey === `${order_id}_order_price`}
              onFocus={() => setEditOrderKey(`${order_id}_order_price`)}
              onBlur={resetEditOrderKey}
              onEditOrder={onEditOrder}
              calculateOrderValue={v => BigNumber(v).times(quantity).toNumber()}
            />
          )}
        </td>
        <td className="table-list-cell">
          {formatValue(average_executed_price)}
        </td>
        <td className="table-list-cell">
          {!isOpenOrder ? (
            formatValue(quantity)
          ) : (
            <EditInput
              type="order_quantity"
              symbol={symbol}
              value={BigNumber(quantity).minus(executed).toNumber()}
              isFocus={editOrderKey === `${order_id}_order_quantity`}
              onFocus={() => setEditOrderKey(`${order_id}_order_quantity`)}
              onBlur={resetEditOrderKey}
              onEditOrder={onEditOrder}
              calculateOrderValue={v => BigNumber(price).times(v).toNumber()}
            />
          )}
        </td>
        {!isOpenOrder && (
          <td className="table-list-cell">{formatValue(executed)}</td>
        )}
        <td className="table-list-cell">{formatValue(orderValue)}</td>
        <td className="table-list-cell">
          {total_fee} <span className="fee-asset-label">{fee_asset}</span>
        </td>
        {isAllOrder && (
          <td className="table-list-cell capitalize">{orderStatus}</td>
        )}
        <td className="table-list-cell">{order_id}</td>
        <td className="table-list-cell align-left">
          {type === ORDER_TYPE_MAP.postOnly ? 'Yes' : '-'}
        </td>
        <td className="table-list-cell align-left">
          {reduce_only ? 'Yes' : '-'}
        </td>
        <td className="table-list-cell align-left">
          {isHiddenOrder ? 'Yes' : '-'}
        </td>
        <td className="table-list-cell align-right">
          {format(new Date(updated_time), 'yyyy-MM-dd HH:mm:ss')}
        </td>
        <td
          className="table-list-cell order-detail-toggle-button"
          onClick={() => hasDetail && toggleOrderDetail()}
        >
          {hasDetail && <IconChevronDown className="icon-chevron-down" />}
        </td>
        {isOpenOrder && (
          <td className="table-list-cell align-right action-column">
            <button
              className="cancel-order-button"
              onClick={() => cancelOrder(symbol, order_id)}
            >
              Cancel
            </button>
          </td>
        )}
      </tr>
      {isOrderDetailActive && OrderDetailRowHeader}
      {isOrderDetailActive && OrderDetailRowList}
    </>
  )
}
